import {
  ResponsiveContainer,
  BarChart, Bar,
  LineChart, Line,
  PieChart, Pie, Cell,
  AreaChart, Area,
  ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts'
import type { ChartConfig } from '../types'

// Default color palette when series don't specify colors
const DEFAULT_COLORS = [
  '#4F81BD', '#C0504D', '#9BBB59', '#8064A2', '#4BACC6',
  '#F79646', '#2C4D75', '#772C2A', '#5F7530', '#4D3B62',
]

interface ChartRendererProps {
  config: ChartConfig
  className?: string
}

export function ChartRenderer({ config, className }: ChartRendererProps) {
  const {
    chartType, title, data, categoryKey, series,
    width, height = 300, legend: showLegend = true, stacked = false,
  } = config

  const getColor = (i: number) => series[i]?.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length]

  const chart = (() => {
    switch (chartType) {
      case 'bar':
        return (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={categoryKey} />
            <YAxis />
            <Tooltip />
            {showLegend && <Legend />}
            {series.map((s, i) => (
              <Bar
                key={s.dataKey}
                dataKey={s.dataKey}
                name={s.name}
                fill={getColor(i)}
                stackId={stacked ? 'stack' : undefined}
              />
            ))}
          </BarChart>
        )

      case 'line':
        return (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={categoryKey} />
            <YAxis />
            <Tooltip />
            {showLegend && <Legend />}
            {series.map((s, i) => (
              <Line
                key={s.dataKey}
                type="monotone"
                dataKey={s.dataKey}
                name={s.name}
                stroke={getColor(i)}
                dot={data.length <= 20}
              />
            ))}
          </LineChart>
        )

      case 'area':
        return (
          <AreaChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={categoryKey} />
            <YAxis />
            <Tooltip />
            {showLegend && <Legend />}
            {series.map((s, i) => (
              <Area
                key={s.dataKey}
                type="monotone"
                dataKey={s.dataKey}
                name={s.name}
                fill={getColor(i)}
                stroke={getColor(i)}
                fillOpacity={0.3}
                stackId={stacked ? 'stack' : undefined}
              />
            ))}
          </AreaChart>
        )

      case 'pie': {
        // Pie uses first series only; categoryKey labels the slices
        const dataKey = series[0]?.dataKey || 'value'
        return (
          <PieChart>
            <Tooltip />
            {showLegend && <Legend />}
            <Pie
              data={data}
              dataKey={dataKey}
              nameKey={categoryKey}
              cx="50%"
              cy="50%"
              outerRadius="80%"
              label={data.length <= 10}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={DEFAULT_COLORS[i % DEFAULT_COLORS.length]} />
              ))}
            </Pie>
          </PieChart>
        )
      }

      case 'scatter':
        return (
          <ScatterChart>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={categoryKey} name={categoryKey} type="number" />
            <YAxis dataKey={series[0]?.dataKey} name={series[0]?.name} type="number" />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} />
            {showLegend && <Legend />}
            {series.map((s, i) => (
              <Scatter
                key={s.dataKey}
                name={s.name}
                data={data}
                fill={getColor(i)}
              />
            ))}
          </ScatterChart>
        )

      default:
        return <div style={{ padding: 16, color: '#888' }}>Unsupported chart type: {chartType}</div>
    }
  })()

  return (
    <div className={className}>
      {title && (
        <div style={{ textAlign: 'center', fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
          {title}
        </div>
      )}
      {width ? (
        <ResponsiveContainer width={width} height={height}>
          {chart}
        </ResponsiveContainer>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          {chart}
        </ResponsiveContainer>
      )}
    </div>
  )
}
